<template>
    <UTooltip :text="showError? 'Kunde må angis' : ''">
        <USelectMenu
            v-model="subAccount"
            v-model:search-term="searchTerm"
            :items="sortedAccounts"
            :size
            :autofocus
            :default-open
            :color="showError ? 'error': 'neutral'"
            :highlight="showError"
            :ui="{ content: 'min-w-fit '+theme}"
            :placeholder="isCustomer ? 'Velg kunde' : 'Velg leverandør'"
            label-key="name"
            virtualize
            ignore-filter
            create-item="always"
            class="w-full"
            @create="addSubAccount"
            @blur="touched = true"
        >
            <template #item="{item}">
                <span class="font-bold min-w-8">
                    {{ item.subAccountNo }}
                </span>
                <span class="ml-1 text-highlighted" data-cy="invoice-creator-account-picker-item">
                    {{ item.name }}
                </span>
            </template>
            <template #content-bottom>
                <UButton
                    size="sm"
                    color="neutral"
                    variant="outline"
                    class="w-fit m-1"
                    :icon="isCustomer ? 'i-ph-user-plus' : 'i-ph-truck-plus'"
                    @click="addSubAccount('')"
                >
                    Ny {{ isCustomer ? 'kunde' : 'leverandør' }}
                </UButton>
            </template>
        </USelectMenu>
    </UTooltip>
</template>

<script setup lang="ts">
import {customers, suppliers} from '@/subaccount/subAccount.service'
import {CUSTOMER, SubAccount, SubAccountRelationType} from '@/subaccount/types/SubAccount'
import {computed, ref} from 'vue'
import {defaultNewCustomer, defaultNewSupplier} from '@/subaccount/SubAccount.builder'
import NewSubAccountPopup from '@/subaccount/NewSubAccountPopup.vue'
import useDynamicComponentLoader from '@/dynamic-component-loader/dynamicComponent.service'

const {loadComponent, clearDynamicComponent} = useDynamicComponentLoader()

const props = defineProps({
    relationType: String as () => SubAccountRelationType,
    size: {type: String, default: 'md'},
    autofocus: {type: Boolean, default: false},
    defaultOpen: {type: Boolean, default: false},
    required: {type: Boolean, default: true},
    theme: {type: String, default: ''}  //TODO: Type to available theme classes?
})
const emit = defineEmits(['created', 'updated'])
const touched = ref(false)

const isCustomer = computed(() => props.relationType === CUSTOMER)
const subAccount = defineModel<SubAccount | undefined>('subAccount')

const searchTerm = ref('')


const filteredAccounts = computed(() => {
    const accounts = isCustomer.value ? customers.value : suppliers.value
    if (!searchTerm.value) return accounts
    const lowerSearch = searchTerm.value.toLowerCase()
    return accounts
        .filter(a => a.active)
        .filter(account => {
            return account.name.toLowerCase().includes(lowerSearch) ||
                account.subAccountNo.toString().includes(lowerSearch)
        })
})

const sortedAccounts = computed(() => {
    return [...filteredAccounts.value].sort((a, b) => {
        return Number(a.subAccountNo) > Number(b.subAccountNo) ? 1 : -1
    })
})

const addSubAccount = (newSubAccName: string) => {
    loadComponent(
        NewSubAccountPopup,
        {
            ...(isCustomer.value ? defaultNewCustomer() : defaultNewSupplier()),
            name: newSubAccName
        },
        {
            created: (sacc: SubAccount) => {
                subAccount.value = sacc
                emit('created', sacc)
            },
            updated: (sacc: SubAccount) => {
                subAccount.value = sacc
                emit('updated', sacc)
            },
            close: () => {
                clearDynamicComponent()
            }
        }
    )
}

const showError = computed(() => {
    return touched.value && props.required && !subAccount.value
})

</script>

<style scoped>

</style>